/**
 * Pure layout table for multi-panel sliding doors (2–5 panels).
 *
 * Layout per the 2026-05-22 spec (see
 * `claude-docs/2026-05-22-pocket-sliding-door-convention.html`), extended
 * to 5 panels:
 *   - 2 panels: [fixed | slider]. Two face-to-face layers at ±t/2.
 *     Slider on the interior.
 *   - 3 panels: [fixed | slider | slider]. Three layers at −t / 0 / +t;
 *     sliders cascade toward the handle side.
 *   - 4 panels: [fixed | slider | slider | fixed]. Two layers; outer
 *     pair on the exterior, inner pair on the interior. Inner sliders
 *     bi-part outward over their neighboring fixed panels.
 *   - 5 panels: a telescoping slider — one fixed end panel, four
 *     sliders that all stack together over it, one layer per panel
 *     (five layers, so a deep track assembly, like the real thing).
 *     An odd count cannot bi-part — only even counts can meet in the
 *     middle.
 *
 * All values are unitless factors: `zFactors` are multiples of the
 *  panel thickness, `slideFactors` are multiples of the panel stride
 * (panelWidth − overlap). The caller scales them.
 *
 * `handleEdges[i]` is 'left' | 'right' | null — a non-null entry puts a
 * finger pull at that edge of slider i.
 */
export function computeSlidingPanelLayout(slidingPanelCount, isLeftHand) {
  const N = Math.max(2, Math.min(5, slidingPanelCount || 2));

  let zFactors;
  let fixedIndices;
  let handleEdges;
  let slideFactors;
  if (N === 2) {
    if (isLeftHand) {
      // [slider | fixed]
      zFactors = [+0.5, -0.5];
      fixedIndices = new Set([1]);
      handleEdges = ['left', null];
      slideFactors = [+1, 0];
    } else {
      // [fixed | slider]
      zFactors = [-0.5, +0.5];
      fixedIndices = new Set([0]);
      handleEdges = [null, 'right'];
      slideFactors = [0, -1];
    }
  } else if (N === 3) {
    if (isLeftHand) {
      // [slider | slider | fixed] — sliders cascade from interior to
      // exterior; the outermost (leftmost) slider carries the pull.
      // Both sliders open RIGHT, stacking with the fixed.
      zFactors = [+1, 0, -1];
      fixedIndices = new Set([2]);
      handleEdges = ['left', null, null];
      slideFactors = [+2, +1, 0];
    } else {
      // [fixed | slider | slider]
      zFactors = [-1, 0, +1];
      fixedIndices = new Set([0]);
      handleEdges = [null, null, 'right'];
      slideFactors = [0, -1, -2];
    }
  } else if (N === 4) {
    // Always [fixed | slider | slider | fixed]. Inner sliders' pulls sit
    // on their INNER edges (where the two sliders meet at the doorway
    // center). When opened, each inner slider moves outward, overlapping
    // its neighboring outer fixed.
    zFactors = [-0.5, +0.5, +0.5, -0.5];
    fixedIndices = new Set([0, 3]);
    handleEdges = [null, 'right', 'left', null];
    slideFactors = [0, -1, +1, 0];
  } else {
    // N === 5 — a telescoping slider: one fixed end panel and four
    // sliders that all stack together over it when open, one layer per
    // panel (an odd count cannot bi-part — only even counts can meet
    // in the middle; this also matches the 2D floor-plan symbol).
    // Five face-to-face layers make a genuinely deep track assembly,
    // as real 5-panel telescoping doors are. Layers step monotonically
    // from the lead slider on the interior to the fixed panel on the
    // exterior, mirroring the N=3 convention. The pull sits on the
    // lead slider's outer edge, like N=2 / N=3.
    if (isLeftHand) {
      // [slider | slider | slider | slider | fixed], all open RIGHT
      zFactors = [+2, +1, 0, -1, -2];
      fixedIndices = new Set([4]);
      handleEdges = ['left', null, null, null, null];
      slideFactors = [+4, +3, +2, +1, 0];
    } else {
      // [fixed | slider | slider | slider | slider], all open LEFT
      zFactors = [-2, -1, 0, +1, +2];
      fixedIndices = new Set([0]);
      handleEdges = [null, null, null, null, 'right'];
      slideFactors = [0, -1, -2, -3, -4];
    }
  }

  const layerCount = new Set(zFactors).size;
  return { N, zFactors, fixedIndices, handleEdges, slideFactors, layerCount };
}
